import * as Sentry from "@sentry/react";
import {
	type CSSProperties,
	type PointerEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EdHubDetailShell } from "../components/education/EdHubDetailShell";
import { useAnalytics } from "../lib/analytics";
import { readCardIndex, writeCardIndex } from "../lib/edhub/cardProgress";
import { formatToSegment } from "../lib/edhub/formatToSegment";
import { useClientTranslations } from "../lib/i18n/client";
import {
	type CardDeck,
	type EducationHubArticleDetail,
	fetchArticleDetail,
	fetchCardDeck,
} from "../services/educationService";
import styles from "./EducationCards.module.css";

const SWIPE_THRESHOLD = 50;

/**
 * Flashcards viewer. Figma node 2420:25554.
 *
 * The deck lives in a JSON file (prism `CardDeck` schema) referenced by the
 * detail row's `cardsUrl`. Until the prism cards worker lands the artefact,
 * `cardsUrl` is null (or the artefact 404s) and we show a pending state.
 *
 * Reading position persists per content id in localStorage so a learner
 * returns to the card they left off on; with no saved progress the viewer
 * opens on the first card (86ey1dqwx — first card is the no-progress default).
 */
export function EducationCards() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { logEducation } = useAnalytics();
	const { t, direction } = useClientTranslations("education");
	const [detail, setDetail] = useState<EducationHubArticleDetail | null>(null);
	const [deck, setDeck] = useState<CardDeck | null>(null);
	const [index, setIndex] = useState(() => (id ? readCardIndex(id) : 0));
	const dragStartX = useRef<number | null>(null);

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (hasTrackedPageView.current) return;
		hasTrackedPageView.current = true;
		logEducation("cards_page_viewed", { contentId: id });
	}, [logEducation, id]);

	// Reset per-deck state when the route id changes so a new deck never shows
	// the previous deck's cards/detail; seed the index from saved progress (0 if
	// none) so we resume where the learner left off, defaulting to the first card.
	useEffect(() => {
		setDetail(null);
		setDeck(null);
		setIndex(id ? readCardIndex(id) : 0);
	}, [id]);

	useEffect(() => {
		if (!id) return;
		let cancelled = false;
		fetchArticleDetail(id)
			.then(async (d) => {
				if (cancelled) return;
				setDetail(d);
				if (d.cardsUrl) {
					try {
						const json = await fetchCardDeck(d.cardsUrl);
						if (!cancelled) setDeck(json);
					} catch (err) {
						// Deck still rendering, unparseable, or fetch failed — keep the
						// pending state, but surface the error so a device-only failure
						// (e.g. cross-origin block) is diagnosable instead of invisible.
						console.error("education cards fetch failed", err);
						Sentry.captureException(err);
					}
				}
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [id]);

	const cards = deck?.cards ?? [];
	const hasCards = cards.length > 0;

	// Keep index inside [0, cards.length - 1] once the deck loads.
	useEffect(() => {
		if (!hasCards) return;
		setIndex((i) => Math.min(Math.max(i, 0), cards.length - 1));
	}, [hasCards, cards.length]);

	// Persist reading position whenever the index changes.
	useEffect(() => {
		if (id) writeCardIndex(id, index);
	}, [id, index]);

	const go = (next: number) => {
		if (!hasCards) return;
		setIndex(Math.min(Math.max(next, 0), cards.length - 1));
	};

	const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
		dragStartX.current = e.clientX;
	};
	const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
		if (dragStartX.current == null) return;
		// In RTL reading order "forward" is to the LEFT, so a rightward swipe
		// advances (mirror of LTR). Invert the delta instead of branching below.
		const rtl = direction === "rtl";
		const dx = (e.clientX - dragStartX.current) * (rtl ? -1 : 1);
		dragStartX.current = null;
		if (dx < -SWIPE_THRESHOLD) go(index + 1);
		else if (dx > SWIPE_THRESHOLD) go(index - 1);
	};
	const onKeyDown = (e: { key: string }) => {
		const forward = direction === "rtl" ? "ArrowLeft" : "ArrowRight";
		const back = direction === "rtl" ? "ArrowRight" : "ArrowLeft";
		if (e.key === forward) go(index + 1);
		else if (e.key === back) go(index - 1);
	};

	const current = hasCards ? cards[index] : null;

	return (
		<EdHubDetailShell
			availableFormats={["text", "cards", "audio"]}
			onFormatSelect={(format) =>
				id && navigate(`/education/${id}/${formatToSegment(format)}`)
			}
			onClose={() => navigate("/education")}
			closeLabel={t("article.close", "Close")}
		>
			<div className={styles.content}>
				{!detail && (
					<p className={styles.loading}>{t("article.loading", "Loading…")}</p>
				)}
				{detail && !hasCards && (
					<div className={styles.pending}>
						<p>
							{t(
								"cards.pending",
								"Flashcards are still being prepared by the AI pipeline.",
							)}
						</p>
					</div>
				)}
				{current && (
					<>
						<div
							className={styles.swipe}
							role="application"
							aria-roledescription="flashcard carousel"
							aria-label={t("cards.carousel", "Flashcards")}
							// biome-ignore lint/a11y/noNoninteractiveTabindex: the carousel is a custom keyboard-interactive widget (Left/Right arrows change card) and must be focusable to receive key events
							tabIndex={0}
							onPointerDown={onPointerDown}
							onPointerUp={onPointerUp}
							onKeyDown={onKeyDown}
						>
							{/* All cards live in one flex track that slides on index change, so
							    switching animates like a carousel (not a hard swap). Rendering
							    every card also makes the viewport as tall as the tallest card,
							    so a shorter card never shifts the section below it. The
							    translate sign flips for RTL (positive x reveals later slides). */}
							<div className={styles.viewport}>
								<div
									className={styles.track}
									style={
										{
											"--track-offset": `${index * (direction === "rtl" ? 100 : -100)}%`,
										} as CSSProperties
									}
								>
									{cards.map((card, i) => (
										<div
											key={`card-${card.number}`}
											className={styles.slide}
											aria-hidden={i !== index}
										>
											<div className={styles.card}>
												<div className={styles.numberBadge}>{card.number}</div>
												<h2 className={styles.cardTitle}>{card.question}</h2>
												<p className={styles.cardBody}>{card.answer}</p>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
						<div className={styles.dots}>
							{cards.map((card, i) => (
								<button
									key={`dot-${card.number}`}
									type="button"
									className={`${styles.dot} ${i === index ? styles.dotActive : ""}`}
									onClick={() => go(i)}
									aria-label={`Card ${i + 1}`}
								/>
							))}
						</div>
						{deck && (
							<section className={styles.contextBlock}>
								<h3 className={styles.articleHeading}>{deck.title}</h3>
								<p className={styles.articleBody}>{deck.context}</p>
							</section>
						)}
					</>
				)}
			</div>
		</EdHubDetailShell>
	);
}
